/**
 * Browser binding for the headless camera operator.
 *
 * The decision policy itself lives in `@archlang/scene/board` (pure, DOM-free,
 * unit-tested without a browser). This module is the thin glue that:
 *   1. turns successive compiled models into the operator's normalised
 *      `OperatorSignal`s (`useOperatorSignals`), and
 *   2. drives the operator off those signals and applies its decisions through
 *      the viewport's animation loop (`useCameraOperator`).
 *
 * Timestamps come from `performance.now()` here (kept out of the pure operator),
 * so the operator's suppression window and the manual-gesture notifications
 * share one clock.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createCameraOperator,
  createFocusDirector,
  type CameraOperator,
  type FocusDirector,
  type FrameTarget,
  type OperatorDecision,
  type OperatorScene,
  type OperatorSignal,
} from "@archlang/scene/board";
import type { SpaceLike, Viewport } from "@archlang/scene/board";
import { asSource, type BoardSpace } from "@archlang/scene/board";
import {
  edgeFromKey,
  edgeToKey,
  flattenModulesWithParent,
  moduleKey,
  type ArchitectureModel,
  type DependencyEdge,
  type Module,
  type Process,
} from "@archlang/scene/arch-model";
import { processKey } from "../processKey.js";

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : 0;
}

// --- model → signal --------------------------------------------------------

/**
 * JSON replacer that drops source-position keys. Wire objects (steps, fields)
 * pass raw core data through, and core decorates nearly everything with
 * `span`/`source` SourceSpans. Spans are POSITIONS, not content: typing
 * anywhere shifts the line numbers of everything below the caret, so a
 * fingerprint that includes spans marks the whole rest of the file as
 * "changed" on every keystroke (the bug: editing a top-of-file note framed the
 * process cluster below it instead of the note).
 */
const dropSpans = (key: string, value: unknown): unknown =>
  key === "span" || key === "source" ? undefined : value;

/**
 * A cheap content fingerprint for a single module's OWN data (its children are
 * separate flattened entries, so they're fingerprinted independently). Two
 * compiles that produce the same fingerprint for a key mean that node didn't
 * change — so a keystroke maps to the small set of nodes that actually moved.
 */
function moduleSignature(m: Module): string {
  return JSON.stringify(
    {
      n: m.name,
      t: m.typeName,
      b: m.baseType,
      d: m.description ?? "",
      f: m.fields,
      l: m.aspects,
      i: m.interfaces.map((iface) => `${iface.typeName}:${iface.name}`),
      s: m.surfaces.map((surf) => surf.name),
      syn: m.synthesized ?? false,
      un: m.unnamed ?? false,
    },
    dropSpans,
  );
}

/**
 * Diff a model's flattened modules against the previous fingerprint map.
 * Returns the board-keys whose content changed or newly appeared (removals are
 * intentionally omitted — there's no surviving node to frame), plus the fresh
 * fingerprint map to carry forward.
 */
export function diffModuleSignatures(
  prev: ReadonlyMap<string, string>,
  modules: readonly Module[],
): { readonly changed: string[]; readonly sigs: Map<string, string> } {
  const sigs = new Map<string, string>();
  for (const m of flattenModulesWithParent(modules)) {
    sigs.set(moduleKey(m), moduleSignature(m));
  }
  const changed: string[] = [];
  for (const [key, sig] of sigs) {
    if (prev.get(key) !== sig) changed.push(key);
  }
  return { changed, sigs };
}

// --- process span ----------------------------------------------------------

/** The board ids a process spans — every module that appears as a step
 *  endpoint, matched to the same key the board items use (`id` ?? dotted path).
 *  So "editing a process" can frame the whole region it touches. */
export function processSpanIds(
  edges: readonly DependencyEdge[],
  process: Pick<Process, "id" | "name">,
): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  const add = (key: string | undefined): void => {
    if (key && !seen.has(key)) {
      seen.add(key);
      ids.push(key);
    }
  };
  for (const e of edges) {
    // The id is authoritative when present — matching by name as well would
    // vacuum up a DIFFERENT process that happens to share the short name
    // (and, for anonymous processes, every empty-name edge). Fall back to the
    // name only when the process carries no id.
    const matches =
      process.id !== undefined
        ? e.processId === process.id
        : e.processName === process.name;
    if (!matches) continue;
    add(edgeFromKey(e));
    add(edgeToKey(e));
  }
  return ids;
}

function processSignature(p: Process): string {
  // dropSpans is load-bearing here: steps are raw core objects carrying
  // `source`/`span` positions, which shift whenever anything above the process
  // is edited — without stripping them, every keystroke higher in the file
  // reads as "this process changed".
  return JSON.stringify(
    {
      n: p.name,
      id: p.id ?? "",
      d: p.description ?? "",
      steps: p.steps,
      subs: p.subprocesses ?? [],
    },
    dropSpans,
  );
}

/** Diff processes by content fingerprint — a process edit doesn't change any
 *  module fingerprint, so it needs its own diff to be noticed. Returns the
 *  changed process objects (not just keys) so the caller can resolve their
 *  spans. */
export function diffProcessSignatures(
  prev: ReadonlyMap<string, string>,
  processes: readonly Process[],
): { readonly changed: Process[]; readonly sigs: Map<string, string> } {
  const sigs = new Map<string, string>();
  const changed: Process[] = [];
  // Disambiguate colliding keys by document order. Bare scratch steps
  // (`Client > Shortener`) each synthesize an ANONYMOUS process — every one of
  // them has the same empty processKey, so a plain map kept only the last
  // fingerprint and the rest read as "changed" on every compile (which made the
  // process-priority branch eat every edit and frame the flow cluster).
  const counts = new Map<string, number>();
  for (const p of processes) {
    const base = processKey(p);
    const n = counts.get(base) ?? 0;
    counts.set(base, n + 1);
    const key = n === 0 ? base : `${base}#${n}`;
    const sig = processSignature(p);
    sigs.set(key, sig);
    if (prev.get(key) !== sig) changed.push(p);
  }
  return { changed, sigs };
}

export interface OperatorSignals {
  readonly editSignal: OperatorSignal | null;
  readonly selectionSignal: OperatorSignal | null;
}

/**
 * Derive the operator's edit + selection signals.
 *
 * **edit** comes from a per-compile module-fingerprint diff: the first compile
 * (and any compile that changes nothing) produces no signal, so loading a
 * workspace never triggers motion — only genuine post-load edits do.
 *
 * **selection** comes from `selectionIds` — the module(s) covered by the
 * *editor's* text selection (mapped upstream from the selected line range).
 * Board/sidebar clicks deliberately do NOT feed this: a board click lands on a
 * node the user is already looking at, so framing it makes the view lurch on
 * every click. Selecting *code* is the intentional "frame this" gesture. Pass an
 * empty list to never frame on selection (e.g. a read-only embed with no editor).
 */
export function useOperatorSignals(
  model: ArchitectureModel | null,
  structural: boolean,
  selectionIds: readonly string[],
  active: boolean,
  /** Dependency edges — used to resolve a *process* edit to the region it spans
   *  (editing a process frames the whole space it touches). */
  edges: readonly DependencyEdge[],
  /** Workspace identity. A change is a fresh load, not an edit — history resets
   *  so the swap doesn't emit a spurious "everything changed" signal that would
   *  fight the board's re-fit. */
  resetKey?: unknown,
): OperatorSignals {
  const [editSignal, setEditSignal] = useState<OperatorSignal | null>(null);
  const prevSigs = useRef<Map<string, string>>(new Map());
  const prevProcSigs = useRef<Map<string, string>>(new Map());
  const seenFirst = useRef(false);
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  // Treat a workspace swap like the first load: forget fingerprints + re-arm the
  // skip-first guard, so the post-swap compile produces no edit signal.
  const prevResetKey = useRef(resetKey);
  if (!Object.is(prevResetKey.current, resetKey)) {
    prevResetKey.current = resetKey;
    prevSigs.current = new Map();
    prevProcSigs.current = new Map();
    seenFirst.current = false;
  }

  useEffect(() => {
    if (!active || !model) return;
    const { changed, sigs } = diffModuleSignatures(prevSigs.current, model.modules);
    prevSigs.current = sigs;
    const { changed: changedProcs, sigs: procSigs } = diffProcessSignatures(
      prevProcSigs.current,
      model.processes ?? [],
    );
    prevProcSigs.current = procSigs;
    // Skip the very first compile (everything is "new") — that's a load, not an
    // edit. A genuine later edit touches a small set and flies/holds per policy.
    if (!seenFirst.current) {
      seenFirst.current = true;
      return;
    }
    // A process edit frames the whole region the process spans (takes priority
    // over module edits — they rarely co-occur, and "show the flow" wins).
    if (changedProcs.length > 0) {
      const span = new Set<string>();
      for (const p of changedProcs) {
        for (const id of processSpanIds(edgesRef.current, p)) span.add(id);
      }
      if (span.size > 0) {
        setEditSignal({ type: "edit", changedIds: [...span], structural, at: now(), region: true });
        return;
      }
    }
    if (changed.length === 0) return;
    setEditSignal({ type: "edit", changedIds: changed, structural, at: now() });
  }, [model, structural, active]);

  // Selection signal: frame the module(s) the editor selection covers. Keyed by
  // the id set so re-selecting the same span doesn't re-emit; null when empty.
  const selectionKey = [...selectionIds].sort().join(" ");
  const selectionSignal = useMemo<OperatorSignal | null>(
    () =>
      active && selectionIds.length > 0
        ? { type: "selection", ids: [...selectionIds], at: now() }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectionKey, active],
  );

  return { editSignal, selectionSignal };
}

// --- focal-path fallback ---------------------------------------------------

/** Walk the (materialised) space tree for the focal path of the space that
 *  directly contains `id`. Fallback for when no model-derived `moduleFocalPath`
 *  is supplied — only finds nodes the lazy source has already solved. */
export function findContainingFocalPath(
  source: SpaceLike,
  id: string,
): readonly string[] | null {
  const root = asSource(source).at([]);
  const walk = (s: BoardSpace, path: string[]): readonly string[] | null => {
    const here = [...path, s.id];
    for (const it of s.items) {
      if (it.id === id) return here;
      if (it.space) {
        const found = walk(it.space, here);
        if (found) return found;
      }
    }
    return null;
  };
  return walk(root, []);
}

// --- the binding hook ------------------------------------------------------

export interface CameraOperatorBinding {
  /** Whether the operator may move the viewport (the UI toggle). */
  readonly enabled: boolean;
  /** Latest edit signal (new identity per emission); null when idle. */
  readonly editSignal: OperatorSignal | null;
  /** Latest selection signal (new identity per emission); null when none. */
  readonly selectionSignal: OperatorSignal | null;
  /** Board ids the editor caret currently sits inside (innermost first; empty
   *  when outside any declaration). Lets the director hold a framed node while
   *  the caret is parked in it, instead of drifting back mid-thought. */
  readonly caretIds?: readonly string[];
  /** Emitted each time the editor selection is CLEARED — triggers the peek
   *  drift-back. `nonce` is a monotonic counter (new clear = new identity);
   *  `graceful` says whether the caret landed inside the framed node (grace
   *  the return so typing can take over) or away from it (return now). */
  readonly selectionCleared: { readonly nonce: number; readonly graceful: boolean } | null;
  readonly source: SpaceLike;
  readonly getViewport: () => Viewport;
  readonly screenSize: () => { width: number; height: number };
  readonly focalPathOf: (id: string) => readonly string[] | null;
  /** Scope-dial expansion (reads the live altitude). Maps the raw edited id(s)
   *  to the ring target framed at that altitude; omitted ⇒ frame ids as-is. */
  readonly expandTarget?: (ids: readonly string[]) => FrameTarget;
  /** Smooth, interruptible move within the current focal. */
  readonly animateTo: (vp: Viewport) => void;
  /** Instant move — used for cross-focal targets (lerp is meaningless). */
  readonly setViewportImmediate: (vp: Viewport) => void;
  /** Workspace identity; a change resets the operator's edit history. */
  readonly resetKey?: unknown;
}

export interface CameraOperatorHandle {
  /** Pass to the viewport's `onUserGesture` so a hand on the board pauses the
   *  operator briefly. Stable identity. */
  readonly noteUserNavigation: () => void;
  /** Spotlight hold — begin a "point faster" session (captures home lazily). */
  readonly spotlightStart: () => void;
  /** Spotlight hold — frame `ids` now (no-op when no hold is active). */
  readonly spotlightTarget: (ids: readonly string[]) => void;
  /** Spotlight hold, overview variant (⌥⇧) — frame the space that CONTAINS
   *  `anchorId` (or the current root when null); the "where am I?" glance. */
  readonly spotlightOverview: (anchorId: string | null) => void;
  /** Spotlight hold — release: glide back to where the hold began. */
  readonly spotlightEnd: () => void;
  /** Scope-dial / frame-now press — frame a ring target now (drops any pending
   *  drift-back). Works whether or not the toggle is on (explicit gesture). */
  readonly frameAtRing: (target: FrameTarget) => void;
}

export function useCameraOperator(binding: CameraOperatorBinding): CameraOperatorHandle {
  const opRef = useRef<CameraOperator | null>(null);
  const dirRef = useRef<FocusDirector | null>(null);
  if (!opRef.current) {
    opRef.current = createCameraOperator();
    dirRef.current = createFocusDirector(opRef.current);
  }

  // Read the live binding through a ref so the signal effects can fire purely
  // on a *new signal* without re-running when unrelated fields change identity.
  const bindingRef = useRef(binding);
  bindingRef.current = binding;

  // Pending quiet-check timer — the director is time-aware (drift back when you
  // stop, commit when you don't), so the binder schedules a `tick()` for the
  // moment activity would have gone quiet, and reschedules as activity arrives.
  const tickRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      clearTimeout(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  useEffect(() => {
    opRef.current!.setEnabled(binding.enabled);
    // Disable hygiene (falling edge): muting the operator forgets any in-flight
    // session/home + typing streak, so a later re-enable can't settle a stale
    // session into a stale-home drift. Covers every disable path — the hotkey,
    // the chrome toggle, the mobile switch — since they all flip `enabled`.
    if (!binding.enabled) {
      dirRef.current!.reset();
      opRef.current!.reset();
      clearTick();
    }
  }, [binding.enabled, clearTick]);

  useEffect(() => {
    // Workspace swap: forget edit history so stale typing focus / a pending
    // drift-back from the previous workspace can't influence the new one.
    dirRef.current!.reset();
    opRef.current!.reset();
    clearTick();
  }, [binding.resetKey, clearTick]);

  // Where the last applied move is heading. While that animation is in flight
  // the live viewport is only *passing through*, so a spotlight/peek that
  // captures home mid-flight should use this destination (the real rest) — see
  // FocusDirector's `inFlightDest`. Harmless once the animation settles (dest ==
  // live viewport); a manual gesture clears it so no stale destination survives
  // a pan.
  const flightDestRef = useRef<Viewport | null>(null);

  const buildScene = useCallback((): OperatorScene | null => {
    const b = bindingRef.current;
    const screen = b.screenSize();
    if (screen.width === 0 || screen.height === 0) return null;
    return {
      viewport: b.getViewport(),
      screen,
      source: b.source,
      focalPathOf: b.focalPathOf,
      expandTarget: b.expandTarget,
      inFlightDest: flightDestRef.current ?? undefined,
    };
  }, []);

  const apply = useCallback((decision: OperatorDecision | null) => {
    if (!decision || decision.kind !== "move") return;
    const b = bindingRef.current;
    flightDestRef.current = decision.viewport;
    if (decision.animate) b.animateTo(decision.viewport);
    else b.setViewportImmediate(decision.viewport);
  }, []);

  // Schedule the next quiet check at the director's requested delay; the +slack
  // ensures `now()` is past the threshold when the tick runs. Reschedules itself
  // while a session stays live.
  const scheduleTick = useCallback(() => {
    clearTick();
    const delay = dirRef.current!.nextQuietCheck(now());
    if (delay === null) return;
    tickRef.current = setTimeout(() => {
      tickRef.current = null;
      if (!bindingRef.current.enabled) return;
      const scene = buildScene();
      if (!scene) return;
      apply(dirRef.current!.tick(scene, now()));
      scheduleTick();
    }, delay + 30);
  }, [apply, buildScene, clearTick]);

  // Edit activity → the director decides whether/when to ease in, drift back, or
  // commit. The model recompile is already debounced, so each edit signal is a
  // coalesced burst.
  useEffect(() => {
    const sig = binding.editSignal;
    if (!sig || sig.type !== "edit") return;
    if (!bindingRef.current.enabled) return;
    const scene = buildScene();
    if (!scene) return;
    apply(dirRef.current!.edit(sig.changedIds, scene, sig.at, sig.region ?? false));
    scheduleTick();
  }, [binding.editSignal, apply, buildScene, scheduleTick]);

  // Code selection → frame immediately (peek).
  useEffect(() => {
    const sig = binding.selectionSignal;
    if (!sig || sig.type !== "selection") return;
    if (!bindingRef.current.enabled) return;
    const scene = buildScene();
    if (!scene) return;
    apply(dirRef.current!.selection(sig.ids, scene, sig.at));
    scheduleTick();
  }, [binding.selectionSignal, apply, buildScene, scheduleTick]);

  // Caret moved → tell the director where the cursor is (holds a framed node
  // while the caret is parked in it; lets it settle once the caret leaves).
  // Declared BEFORE the selection-cleared effect so, on a collapsing selection,
  // the director sees the caret first. Keyed on the id set — a caret move within
  // the same node doesn't churn. No decision comes back; a reschedule covers the
  // caret-hold cap.
  const caretKey = (binding.caretIds ?? []).join(" ");
  useEffect(() => {
    if (!bindingRef.current.enabled) return;
    dirRef.current!.caret(bindingRef.current.caretIds ?? [], now());
    scheduleTick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caretKey, scheduleTick]);

  // Selection cleared → glide back (immediately when the caret moved away,
  // grace-delayed when it stayed inside the framed node).
  useEffect(() => {
    const cleared = binding.selectionCleared;
    if (!cleared || cleared.nonce === 0) return; // initial mount, nothing to clear
    if (!bindingRef.current.enabled) return;
    const scene = buildScene();
    if (!scene) return;
    apply(dirRef.current!.selectionCleared(scene, now(), cleared.graceful));
    scheduleTick();
  }, [binding.selectionCleared, apply, buildScene, scheduleTick]);

  // Release the timer on unmount.
  useEffect(() => clearTick, [clearTick]);

  const noteUserNavigation = useCallback(() => {
    dirRef.current!.userGesture(now());
    opRef.current!.noteUserNavigation(now());
    flightDestRef.current = null; // gesture supremacy — no stale destination
    clearTick();
  }, [clearTick]);

  // Spotlight / dial / frame-now verbs — imperative, driven by the hold + hotkey
  // hooks. They are the always-available "show me" gesture: unlike the auto
  // signals, they do NOT gate on `enabled` (the director bypasses the disabled
  // gate for these), so pointing works whether or not autopilot is toggled on.
  const spotlightStart = useCallback(() => {
    dirRef.current!.spotlightStart(now());
    scheduleTick();
  }, [scheduleTick]);
  const spotlightTarget = useCallback(
    (ids: readonly string[]) => {
      const scene = buildScene();
      if (!scene) return;
      apply(dirRef.current!.spotlightTarget(ids, scene, now()));
      scheduleTick();
    },
    [apply, buildScene, scheduleTick],
  );
  const spotlightOverview = useCallback(
    (anchorId: string | null) => {
      const scene = buildScene();
      if (!scene) return;
      const b = bindingRef.current;
      // Frame the space CONTAINING the anchor; with no anchor, the root of the
      // current focal chain (the full picture).
      const spacePath =
        (anchorId ? b.focalPathOf(anchorId) : null) ?? scene.viewport.focalPath.slice(0, 1);
      apply(dirRef.current!.spotlightSpace(spacePath, scene, now()));
      scheduleTick();
    },
    [apply, buildScene, scheduleTick],
  );
  const spotlightEnd = useCallback(() => {
    const scene = buildScene();
    if (!scene) return;
    apply(dirRef.current!.spotlightEnd(scene, now()));
    scheduleTick();
  }, [apply, buildScene, scheduleTick]);

  const frameAtRing = useCallback(
    (target: FrameTarget) => {
      const scene = buildScene();
      if (!scene) return;
      apply(dirRef.current!.dialFrame(target, scene, now()));
      scheduleTick();
    },
    [apply, buildScene, scheduleTick],
  );

  return {
    noteUserNavigation,
    spotlightStart,
    spotlightTarget,
    spotlightOverview,
    spotlightEnd,
    frameAtRing,
  };
}
